import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  listBankAccounts, listBankStatements, createBankStatement, fetchBankStatement,
  importBankStatementCSV, listBankStatementLines, createBankStatementLine,
  matchBankStatementLine, unmatchBankStatementLine,
  listBankReconciliations, createBankReconciliation, completeBankReconciliation,
  listJournalEntries,
} from '../api';
import { formatCurrency } from '../utils/currency';
import { useConfirm } from './ConfirmProvider';
import EmptyState from './shared/EmptyState';
import BankStatementImporter from './BankStatementImporter';

function BankReconciliation({ authAxios, showFormMessage, userRole }) {
  const confirm = useConfirm();
  const [activeTab, setActiveTab] = useState('statements');
  const [bankAccounts, setBankAccounts] = useState([]);
  const [selectedBankAccount, setSelectedBankAccount] = useState('');
  const [statements, setStatements] = useState([]);
  const [selectedStatement, setSelectedStatement] = useState(null);
  const [statementLines, setStatementLines] = useState([]);
  const [reconciliations, setReconciliations] = useState([]);
  const [journalEntries, setJournalEntries] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Statement form
  const [showStatementForm, setShowStatementForm] = useState(false);
  const [statementForm, setStatementForm] = useState({
    bank_account: '', period_start: '', period_end: '', opening_balance: '0.00', closing_balance: '0.00', notes: '',
  });

  // Line form
  const [showLineForm, setShowLineForm] = useState(false);
  const [lineForm, setLineForm] = useState({
    date: '', description: '', reference: '', debit: '0.00', credit: '0.00', balance: '',
  });

  // Recon form
  const [showReconForm, setShowReconForm] = useState(false);
  const [reconForm, setReconForm] = useState({
    bank_account: '', statement: '', period_start: '', period_end: '',
    statement_balance: '0.00', gl_balance: '0.00',
  });

  // Matching state
  const [matchingLineId, setMatchingLineId] = useState(null);

  // Bank Statement Importer (CSV/Excel + auto-mapping wizard, Trust #4).
  const [showImporter, setShowImporter] = useState(false);

  const canManage = useMemo(() => ['PARTNER', 'SUPERUSER', 'ACCOUNTANT'].includes(userRole), [userRole]);

  const fetchBankAccounts = useCallback(async () => {
    try {
      const data = await listBankAccounts(authAxios);
      setBankAccounts(Array.isArray(data) ? data : []);
    } catch { /* ignore */ }
  }, [authAxios]);

  const fetchStatements = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = selectedBankAccount ? { bank_account: selectedBankAccount } : {};
      const data = await listBankStatements(authAxios, params);
      setStatements(Array.isArray(data) ? data : []);
    } catch {
      showFormMessage('error', 'Failed to load statements.');
    } finally {
      setIsLoading(false);
    }
  }, [authAxios, selectedBankAccount, showFormMessage]);

  const fetchLines = useCallback(async (stmtId) => {
    try {
      const data = await listBankStatementLines(authAxios, { statement: stmtId });
      setStatementLines(Array.isArray(data) ? data : []);
    } catch { /* ignore */ }
  }, [authAxios]);

  const fetchRecons = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = selectedBankAccount ? { bank_account: selectedBankAccount } : {};
      const data = await listBankReconciliations(authAxios, params);
      setReconciliations(Array.isArray(data) ? data : []);
    } catch {
      showFormMessage('error', 'Failed to load reconciliations.');
    } finally {
      setIsLoading(false);
    }
  }, [authAxios, selectedBankAccount, showFormMessage]);

  const fetchJournals = useCallback(async () => {
    try {
      const data = await listJournalEntries(authAxios);
      setJournalEntries(Array.isArray(data) ? data : []);
    } catch { /* ignore */ }
  }, [authAxios]);

  useEffect(() => { fetchBankAccounts(); }, [fetchBankAccounts]);
  useEffect(() => {
    if (activeTab === 'statements') fetchStatements();
    else if (activeTab === 'reconciliations') fetchRecons();
  }, [activeTab, fetchStatements, fetchRecons]);

  // Create statement
  const handleCreateStatement = async (e) => {
    e.preventDefault();
    if (statementForm.period_start && statementForm.period_end
        && new Date(statementForm.period_end) < new Date(statementForm.period_start)) {
      showFormMessage('error', 'Period end cannot be before period start.');
      return;
    }
    try {
      await createBankStatement(authAxios, statementForm);
      showFormMessage('success', 'Statement created.');
      setShowStatementForm(false);
      fetchStatements();
    } catch (err) {
      showFormMessage('error', err.response?.data?.detail || 'Failed to create statement.');
    }
  };

  // Import CSV
  const handleImportCSV = async (stmtId) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.csv';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      try {
        const result = await importBankStatementCSV(authAxios, stmtId, file);
        showFormMessage('success', result.detail || 'CSV imported.');
        if (selectedStatement?.id === stmtId) fetchLines(stmtId);
      } catch (err) {
        showFormMessage('error', err.response?.data?.detail || 'CSV import failed.');
      }
    };
    input.click();
  };

  // View statement lines
  const handleViewStatement = async (stmt) => {
    setSelectedStatement(stmt);
    fetchLines(stmt.id);
    fetchJournals();
  };

  // Add manual line
  const handleAddLine = async (e) => {
    e.preventDefault();
    // A bank line is either a debit or a credit — never both at once.
    const debit = parseFloat(lineForm.debit || '0');
    const credit = parseFloat(lineForm.credit || '0');
    if (debit < 0 || credit < 0) {
      showFormMessage('error', 'Debit and credit amounts must be zero or positive.');
      return;
    }
    if (debit > 0 && credit > 0) {
      showFormMessage('error', 'A statement line cannot have both a debit and a credit. Enter only one.');
      return;
    }
    if (debit === 0 && credit === 0) {
      showFormMessage('error', 'Enter either a debit or a credit amount greater than zero.');
      return;
    }
    try {
      await createBankStatementLine(authAxios, { ...lineForm, statement: selectedStatement.id });
      showFormMessage('success', 'Line added.');
      setShowLineForm(false);
      setLineForm({ date: '', description: '', reference: '', debit: '0.00', credit: '0.00', balance: '' });
      fetchLines(selectedStatement.id);
    } catch (err) {
      showFormMessage('error', err.response?.data?.detail || 'Failed to add line.');
    }
  };

  // Match / unmatch
  const handleMatch = async (lineId, jeId) => {
    try {
      await matchBankStatementLine(authAxios, lineId, jeId);
      showFormMessage('success', 'Line matched.');
      setMatchingLineId(null);
      fetchLines(selectedStatement.id);
    } catch (err) {
      showFormMessage('error', err.response?.data?.detail || 'Match failed.');
    }
  };

  const handleUnmatch = async (lineId) => {
    try {
      await unmatchBankStatementLine(authAxios, lineId);
      showFormMessage('success', 'Match removed.');
      fetchLines(selectedStatement.id);
    } catch {
      showFormMessage('error', 'Unmatch failed.');
    }
  };

  // Create reconciliation
  const handleCreateRecon = async (e) => {
    e.preventDefault();
    if (reconForm.period_start && reconForm.period_end
        && new Date(reconForm.period_end) < new Date(reconForm.period_start)) {
      showFormMessage('error', 'Period end cannot be before period start.');
      return;
    }
    try {
      await createBankReconciliation(authAxios, reconForm);
      showFormMessage('success', 'Reconciliation created.');
      setShowReconForm(false);
      fetchRecons();
    } catch (err) {
      showFormMessage('error', err.response?.data?.detail || 'Failed to create reconciliation.');
    }
  };

  const handleCompleteRecon = async (id) => {
    if (!(await confirm({ title: 'Complete Reconciliation', message: 'Complete this reconciliation? This action cannot be undone.', confirmText: 'Complete', variant: 'danger' }))) return;
    try {
      await completeBankReconciliation(authAxios, id);
      showFormMessage('success', 'Reconciliation completed.');
      fetchRecons();
    } catch (err) {
      showFormMessage('error', err.response?.data?.detail || 'Failed to complete.');
    }
  };

  const tabs = [
    { key: 'statements', label: 'Bank Statements' },
    { key: 'reconciliations', label: 'Reconciliations' },
  ];

  return (
    <div className="p-4">
      <h2 className="text-xl font-bold text-gray-800 mb-3">Bank Reconciliation</h2>

      {/* Bank account selector */}
      <div className="mb-4 flex items-center gap-3">
        <label className="text-sm font-medium text-gray-600">Bank Account:</label>
        <select value={selectedBankAccount} onChange={e => setSelectedBankAccount(e.target.value)}
          className="border rounded px-3 py-1.5 text-sm" style={{ minWidth: 220 }}>
          <option value="">All Accounts</option>
          {bankAccounts.map(ba => (
            <option key={ba.id} value={ba.id}>{ba.account_name} – {ba.bank_name}</option>
          ))}
        </select>
      </div>

      {/* Tabs */}
      <div className="flex border-b mb-4">
        {tabs.map(t => (
          <button key={t.key} onClick={() => { setActiveTab(t.key); setSelectedStatement(null); }}
            className={`px-4 py-2 text-sm font-medium ${activeTab === t.key ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ============ STATEMENTS TAB ============ */}
      {activeTab === 'statements' && !selectedStatement && (
        <div>
          {canManage && (
            <div className="mb-3 flex gap-2">
              <button onClick={() => { setStatementForm(f => ({ ...f, bank_account: selectedBankAccount })); setShowStatementForm(true); }}
                className="bg-blue-600 text-white px-3 py-1.5 rounded text-sm hover:bg-blue-700">+ New Statement</button>
              <button onClick={() => setShowImporter(true)}
                className="bg-emerald-600 text-white px-3 py-1.5 rounded text-sm hover:bg-emerald-700"
                title="Upload a CSV or Excel bank statement and let us auto-align the columns">
                ⇪ Import statement (CSV / Excel)
              </button>
            </div>
          )}
          {showImporter && (
            <BankStatementImporter
              authAxios={authAxios}
              bankAccounts={bankAccounts}
              defaultBankAccountId={selectedBankAccount}
              onClose={() => setShowImporter(false)}
              onImported={() => { fetchStatements(); setShowImporter(false); showFormMessage('success', 'Bank statement imported.'); }}
            />
          )}
          {showStatementForm && (
            <div className="bg-white border rounded shadow p-4 mb-4">
              <h3 className="font-semibold mb-2 text-sm">New Bank Statement</h3>
              <form onSubmit={handleCreateStatement} className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Bank Account *</label>
                  <select name="bank_account" value={statementForm.bank_account}
                    onChange={e => setStatementForm(f => ({ ...f, bank_account: e.target.value }))} required
                    className="w-full border rounded px-3 py-2 text-sm">
                    <option value="">Select…</option>
                    {bankAccounts.map(ba => <option key={ba.id} value={ba.id}>{ba.account_name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Period Start *</label>
                  <input type="date" value={statementForm.period_start}
                    onChange={e => setStatementForm(f => ({ ...f, period_start: e.target.value }))} required
                    className="w-full border rounded px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Period End *</label>
                  <input type="date" value={statementForm.period_end}
                    onChange={e => setStatementForm(f => ({ ...f, period_end: e.target.value }))} required
                    className="w-full border rounded px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Opening Balance</label>
                  <input type="number" step="0.01" value={statementForm.opening_balance}
                    onChange={e => setStatementForm(f => ({ ...f, opening_balance: e.target.value }))}
                    className="w-full border rounded px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Closing Balance</label>
                  <input type="number" step="0.01" value={statementForm.closing_balance}
                    onChange={e => setStatementForm(f => ({ ...f, closing_balance: e.target.value }))}
                    className="w-full border rounded px-3 py-2 text-sm" />
                </div>
                <div className="flex items-end gap-2">
                  <button type="submit" className="bg-blue-600 text-white px-4 py-2 rounded text-sm hover:bg-blue-700">Create</button>
                  <button type="button" onClick={() => setShowStatementForm(false)} className="bg-gray-200 px-4 py-2 rounded text-sm">Cancel</button>
                </div>
              </form>
            </div>
          )}

          {isLoading ? <p className="text-gray-500 text-sm">Loading…</p> : (
            <table className="min-w-full bg-white border rounded text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-3 py-2 text-left">Account</th>
                  <th scope="col" className="px-3 py-2 text-left">Period</th>
                  <th scope="col" className="px-3 py-2 text-right">Opening</th>
                  <th scope="col" className="px-3 py-2 text-right">Closing</th>
                  <th scope="col" className="px-3 py-2 text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {statements.map(s => (
                  <tr key={s.id} className="border-t hover:bg-gray-50">
                    <td className="px-3 py-2">{s.bank_account_name}</td>
                    <td className="px-3 py-2">{s.period_start} – {s.period_end}</td>
                    <td className="px-3 py-2 text-right">{formatCurrency(s.opening_balance)}</td>
                    <td className="px-3 py-2 text-right">{formatCurrency(s.closing_balance)}</td>
                    <td className="px-3 py-2 text-center">
                      <button onClick={() => handleViewStatement(s)} className="text-blue-600 text-xs hover:underline mr-2">View Lines</button>
                      {canManage && <button onClick={() => handleImportCSV(s.id)} className="text-green-600 text-xs hover:underline">Import CSV</button>}
                    </td>
                  </tr>
                ))}
                {statements.length === 0 && (
                  <tr><td colSpan={5} className="px-3 py-4">
                    <EmptyState
                      title="No bank statements yet"
                      message="Upload a statement or create one manually to start reconciling transactions."
                      actionLabel="Create statement"
                      onAction={() => setShowStatementForm(true)}
                    />
                  </td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ============ STATEMENT LINES DETAIL ============ */}
      {activeTab === 'statements' && selectedStatement && (
        <div>
          <button onClick={() => setSelectedStatement(null)} className="text-blue-600 text-sm hover:underline mb-3">← Back to Statements</button>
          <h3 className="font-semibold mb-2">
            {selectedStatement.bank_account_name}: {selectedStatement.period_start} – {selectedStatement.period_end}
          </h3>

          {canManage && (
            <div className="mb-3 flex gap-2">
              <button onClick={() => setShowLineForm(true)} className="bg-blue-600 text-white px-3 py-1.5 rounded text-sm hover:bg-blue-700">+ Add Line</button>
              <button onClick={() => handleImportCSV(selectedStatement.id)} className="bg-green-600 text-white px-3 py-1.5 rounded text-sm hover:bg-green-700">Import CSV</button>
            </div>
          )}

          {showLineForm && (
            <div className="bg-white border rounded shadow p-4 mb-4">
              <form onSubmit={handleAddLine} className="grid grid-cols-2 md:grid-cols-6 gap-2">
                <input type="date" value={lineForm.date} onChange={e => setLineForm(f => ({ ...f, date: e.target.value }))}
                  required className="border rounded px-2 py-1.5 text-sm" placeholder="Date" />
                <input value={lineForm.description} onChange={e => setLineForm(f => ({ ...f, description: e.target.value }))}
                  required className="border rounded px-2 py-1.5 text-sm col-span-2" placeholder="Description" />
                <input type="number" step="0.01" value={lineForm.debit} onChange={e => setLineForm(f => ({ ...f, debit: e.target.value }))}
                  className="border rounded px-2 py-1.5 text-sm" placeholder="Debit" />
                <input type="number" step="0.01" value={lineForm.credit} onChange={e => setLineForm(f => ({ ...f, credit: e.target.value }))}
                  className="border rounded px-2 py-1.5 text-sm" placeholder="Credit" />
                <div className="flex gap-1">
                  <button type="submit" className="bg-blue-600 text-white px-3 py-1 rounded text-xs">Add</button>
                  <button type="button" onClick={() => setShowLineForm(false)} className="bg-gray-200 px-3 py-1 rounded text-xs">Cancel</button>
                </div>
              </form>
            </div>
          )}

          <table className="min-w-full bg-white border rounded text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-3 py-2 text-left">Date</th>
                <th scope="col" className="px-3 py-2 text-left">Description</th>
                <th scope="col" className="px-3 py-2 text-left">Reference</th>
                <th scope="col" className="px-3 py-2 text-right">Debit</th>
                <th scope="col" className="px-3 py-2 text-right">Credit</th>
                <th scope="col" className="px-3 py-2 text-right">Balance</th>
                <th scope="col" className="px-3 py-2 text-center">Reconciled</th>
                <th scope="col" className="px-3 py-2 text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {statementLines.map(line => (
                <tr key={line.id} className={`border-t ${line.is_reconciled ? 'bg-green-50' : 'hover:bg-gray-50'}`}>
                  <td className="px-3 py-2">{line.date}</td>
                  <td className="px-3 py-2">{line.description}</td>
                  <td className="px-3 py-2">{line.reference}</td>
                  <td className="px-3 py-2 text-right">{formatCurrency(line.debit)}</td>
                  <td className="px-3 py-2 text-right">{formatCurrency(line.credit)}</td>
                  <td className="px-3 py-2 text-right">{line.balance != null ? formatCurrency(line.balance) : '—'}</td>
                  <td className="px-3 py-2 text-center">
                    {line.is_reconciled ? (
                      <span className="text-green-600 font-medium" title={`Matched: ${line.matched_journal_entry_number || ''}`}>✓</span>
                    ) : '—'}
                  </td>
                  <td className="px-3 py-2 text-center">
                    {line.is_reconciled ? (
                      <button onClick={() => handleUnmatch(line.id)} className="text-red-500 text-xs hover:underline">Unmatch</button>
                    ) : matchingLineId === line.id ? (
                      <div className="flex items-center gap-1">
                        <select className="border rounded text-xs px-1 py-0.5" style={{ maxWidth: 180 }}
                          onChange={e => { if (e.target.value) handleMatch(line.id, e.target.value); }}>
                          <option value="">Select JE…</option>
                          {journalEntries.map(je => (
                            <option key={je.id} value={je.id}>{je.entry_number} – {je.description?.substring(0, 30)}</option>
                          ))}
                        </select>
                        <button onClick={() => setMatchingLineId(null)} className="text-gray-400 text-xs">✗</button>
                      </div>
                    ) : (
                      <button onClick={() => setMatchingLineId(line.id)} className="text-blue-600 text-xs hover:underline">Match</button>
                    )}
                  </td>
                </tr>
              ))}
              {statementLines.length === 0 && (
                <tr><td colSpan={8} className="px-3 py-4 text-center text-gray-400 italic">No lines. Add manually or import CSV.</td></tr>
              )}
            </tbody>
          </table>

          <div className="mt-3 text-sm text-gray-500">
            Reconciled: {statementLines.filter(l => l.is_reconciled).length} / {statementLines.length} lines
          </div>
        </div>
      )}

      {/* ============ RECONCILIATIONS TAB ============ */}
      {activeTab === 'reconciliations' && (
        <div>
          {canManage && (
            <div className="mb-3">
              <button onClick={() => { setReconForm(f => ({ ...f, bank_account: selectedBankAccount })); setShowReconForm(true); }}
                className="bg-blue-600 text-white px-3 py-1.5 rounded text-sm hover:bg-blue-700">+ New Reconciliation</button>
            </div>
          )}

          {showReconForm && (
            <div className="bg-white border rounded shadow p-4 mb-4">
              <h3 className="font-semibold mb-2 text-sm">New Bank Reconciliation</h3>
              <form onSubmit={handleCreateRecon} className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Bank Account *</label>
                  <select value={reconForm.bank_account}
                    onChange={e => setReconForm(f => ({ ...f, bank_account: e.target.value }))} required
                    className="w-full border rounded px-3 py-2 text-sm">
                    <option value="">Select…</option>
                    {bankAccounts.map(ba => <option key={ba.id} value={ba.id}>{ba.account_name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Statement (optional)</label>
                  <select value={reconForm.statement}
                    onChange={e => setReconForm(f => ({ ...f, statement: e.target.value }))}
                    className="w-full border rounded px-3 py-2 text-sm">
                    <option value="">None</option>
                    {statements.map(s => <option key={s.id} value={s.id}>{s.period_start} – {s.period_end}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Period Start *</label>
                  <input type="date" value={reconForm.period_start}
                    onChange={e => setReconForm(f => ({ ...f, period_start: e.target.value }))} required
                    className="w-full border rounded px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Period End *</label>
                  <input type="date" value={reconForm.period_end}
                    onChange={e => setReconForm(f => ({ ...f, period_end: e.target.value }))} required
                    className="w-full border rounded px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Statement Balance *</label>
                  <input type="number" step="0.01" value={reconForm.statement_balance}
                    onChange={e => setReconForm(f => ({ ...f, statement_balance: e.target.value }))} required
                    className="w-full border rounded px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">GL Balance *</label>
                  <input type="number" step="0.01" value={reconForm.gl_balance}
                    onChange={e => setReconForm(f => ({ ...f, gl_balance: e.target.value }))} required
                    className="w-full border rounded px-3 py-2 text-sm" />
                </div>
                <div className="md:col-span-3 flex gap-2">
                  <button type="submit" className="bg-blue-600 text-white px-4 py-2 rounded text-sm hover:bg-blue-700">Create</button>
                  <button type="button" onClick={() => setShowReconForm(false)} className="bg-gray-200 px-4 py-2 rounded text-sm">Cancel</button>
                </div>
              </form>
            </div>
          )}

          {isLoading ? <p className="text-gray-500 text-sm">Loading…</p> : (
            <table className="min-w-full bg-white border rounded text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-3 py-2 text-left">Account</th>
                  <th scope="col" className="px-3 py-2 text-left">Period</th>
                  <th scope="col" className="px-3 py-2 text-right">Statement Bal</th>
                  <th scope="col" className="px-3 py-2 text-right">GL Bal</th>
                  <th scope="col" className="px-3 py-2 text-right">Difference</th>
                  <th scope="col" className="px-3 py-2 text-center">Status</th>
                  {canManage && <th scope="col" className="px-3 py-2 text-center">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {reconciliations.map(r => (
                  <tr key={r.id} className="border-t hover:bg-gray-50">
                    <td className="px-3 py-2">{r.bank_account_name}</td>
                    <td className="px-3 py-2">{r.period_start} – {r.period_end}</td>
                    <td className="px-3 py-2 text-right">{formatCurrency(r.statement_balance)}</td>
                    <td className="px-3 py-2 text-right">{formatCurrency(r.gl_balance)}</td>
                    <td className={`px-3 py-2 text-right font-medium ${Number(r.difference) === 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatCurrency(r.difference)}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <span className={`px-2 py-0.5 rounded-full text-xs ${r.status === 'COMPLETED' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                        {r.status_display || r.status}
                      </span>
                    </td>
                    {canManage && (
                      <td className="px-3 py-2 text-center">
                        {r.status === 'DRAFT' && (
                          <button onClick={() => handleCompleteRecon(r.id)} className="text-green-600 text-xs hover:underline">Complete</button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
                {reconciliations.length === 0 && (
                  <tr><td colSpan={7} className="px-3 py-4 text-center text-gray-400 italic">No reconciliations found.</td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

export default BankReconciliation;
